/**
 * @file Pure confidence→presentation mapping for the Mapping Review UI.
 * Mirrors the legacy mapping-editor color bands (≥0.8 success, ≥0.5 warning,
 * else error) on the shared `CONFIDENCE_WARN_THRESHOLD` from `#types`, so the
 * client highlights exactly what the server counted as `lowConfidence`.
 * Consumed by every review surface that renders a Typesafe confidence Tag
 * (columns, rows, reference resolutions, inline document judgments).
 */
import { CONFIDENCE_HIGH_THRESHOLD, CONFIDENCE_WARN_THRESHOLD } from '#types';

export interface MappingReviewConfidenceView {
  /** AntD status color for the confidence Tag. */
  color: 'success' | 'warning' | 'error';
  /** True at or above `CONFIDENCE_HIGH_THRESHOLD` — the trustworthy green band. */
  high: boolean;
  /** True below `CONFIDENCE_WARN_THRESHOLD` — drives the warning highlight. */
  warn: boolean;
  /** Rounded percent label, e.g. `92%`. */
  label: string;
}

export function mappingReviewConfidence(confidence: number): MappingReviewConfidenceView {
  const color =
    confidence >= CONFIDENCE_HIGH_THRESHOLD
      ? 'success'
      : confidence >= CONFIDENCE_WARN_THRESHOLD
        ? 'warning'
        : 'error';
  return {
    color,
    high: confidence >= CONFIDENCE_HIGH_THRESHOLD,
    warn: confidence < CONFIDENCE_WARN_THRESHOLD,
    label: `${Math.round(confidence * 100)}%`,
  };
}
